import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, useState, lazy, Suspense } from "react";
import { maintenanceConfig } from "./config/maintenance";
import Navbar from "./components/Navbar";
import BackToTop from "./components/BackToTop";
import CookieConsent from "./components/CookieConsent";

// Lazy load pages for better initial performance
const Index = lazy(() => import("./pages/Index"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Docs = lazy(() => import("./pages/Docs"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Support = lazy(() => import("./pages/Support"));
const SupportForm = lazy(() => import("./pages/SupportForm"));
const ArchivedSupport = lazy(() => import("./pages/ArchivedSupport"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Maintenance = lazy(() => import("./pages/Maintenance"));

const queryClient = new QueryClient();

// Simple loading fallback for lazy components
const PageLoader = () => (
  <div className="min-h-screen bg-[#020617] flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-fuchsia-500/20 border-t-fuchsia-500 rounded-full animate-spin" />
  </div>
);

const AppContent = () => {
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasBypassParam = params.get(maintenanceConfig.bypassKey) === "true";
    const hasBypassStorage = localStorage.getItem("maintenance_bypass") === "true";

    if (hasBypassParam) {
      localStorage.setItem("maintenance_bypass", "true");
      setIsAuthorized(true);
    } else if (hasBypassStorage) {
      setIsAuthorized(true);
    }
  }, []);

  if (maintenanceConfig.isMaintenanceMode && !isAuthorized) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Maintenance />
      </Suspense>
    );
  }

  return (
    <BrowserRouter>
      <Navbar />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/docs/:slug" element={<Docs />} />
          <Route path="/roadmap" element={<Roadmap />} />
          <Route path="/support" element={<Support />} />
          <Route path="/support/new" element={<SupportForm />} />
          <Route path="/support/archived" element={<ArchivedSupport />} />
          <Route path="/about" element={<About />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <BackToTop />
      <CookieConsent />
    </BrowserRouter>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AppContent />
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;